import { formatEndDate, getAppRole } from 'utils';

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Bullet from 'components/shared/Bullet';
import { ROLES } from 'constants/global';
import { Poll, PollOption } from 'constants/types';
import { useCheckIfVotedQuery, useVoteMutation } from 'services/voteManagement';

import Tags from '../MeetingBody/components/Tags';
import PollContent from './components/PollContent';
import PollOptions from './components/PollOption';
import { SubmitButton } from './components/PollOption/styles';
import { BodyContainer, PollContainer, VoteDetails } from './styles';

interface PollBodyProps {
  title: string;
  content: string;
  tags: string[];
  pollOptions: PollOption[];
  poll: Poll;
  refetchPinned: () => void;
  refetchUnpinned: () => void;
}

const PollBody: React.FC<PollBodyProps> = ({
  title,
  content,
  pollOptions,
  poll,
  tags,
  refetchPinned,
  refetchUnpinned,
}) => {
  const { t } = useTranslation('forum');
  console.log('pollpoll', poll);
  const [vote] = useVoteMutation();
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [loadingOptions, setLoadingOptions] = useState<string[]>([]);
  const [totalVotes, setTotalVotes] = useState(0);

  const { data: userVotes, refetch } = useCheckIfVotedQuery(poll._id);
  const userVoted = userVotes ? userVotes.length > 0 : false;
  const isStopped = poll.stopped;
  const pollEndDate = poll.endDate ? new Date(poll.endDate) : null;
  const currentDate = new Date();
  const hasExpired = pollEndDate ? currentDate > pollEndDate : false;

  const isDisabled = userVoted || isStopped || hasExpired;
  const formattedEndDate = formatEndDate(poll.endDate);
  const userRole = getAppRole();
  const isRegularUser = userRole !== ROLES.OYO_ADMIN && userRole !== ROLES.PMO;

  useEffect(() => {
    if (userVotes) {
      setSelectedOptions(userVotes);
    }
  }, [userVotes]);

  useEffect(() => {
    const total = pollOptions.reduce(
      (accumulator, option) => accumulator + (option.voteCount || 0),
      0,
    );
    setTotalVotes(total);
  }, [pollOptions]);

  const handleOptionSelect = (pollOptionId: string) => {
    if (!isRegularUser || userVoted) return;
    setSelectedOptions((prev) =>
      poll.multipleAnswers
        ? prev.includes(pollOptionId)
          ? prev.filter((id) => id !== pollOptionId)
          : [...prev, pollOptionId]
        : [pollOptionId],
    );
  };

  const handleVoteSubmit = async () => {
    if (!selectedOptions.length) return;

    setLoadingOptions([...selectedOptions]);
    await vote({ poll: poll._id, choice: selectedOptions });
    setLoadingOptions([]);

    await refetch();
    refetchPinned();
    refetchUnpinned();
  };
  return (
    <BodyContainer>
      <PollContent title={title} content={content} />
      <Tags tags={tags} />
      <VoteDetails>
        {poll.isAnonymous && (
          <>
            {t('anonymous')}
            <Bullet />
          </>
        )}
        {t('total_votes', { count: totalVotes })}
      </VoteDetails>

      {pollOptions && (
        <PollContainer>
          {pollOptions.map((option, index) => {
            const percentage = totalVotes
              ? Math.round(((option.voteCount || 0) / totalVotes) * 100)
              : 0;
            const isSelected = selectedOptions.includes(option._id);
            const isLoading = loadingOptions.includes(option._id);

            return (
              <PollOptions
                key={option._id}
                text={option.text}
                index={index}
                percentage={percentage}
                recentVoters={option.recentVoters || []}
                option={option}
                onSelectOption={() => handleOptionSelect(option._id)}
                isSelected={isSelected}
                disabled={isDisabled}
                loading={isLoading}
                userVoted={userVoted}
                isRegularUser={isRegularUser}
              />
            );
          })}
        </PollContainer>
      )}

      {isRegularUser && !userVoted && selectedOptions.length > 0 && (
        <SubmitButton onClick={handleVoteSubmit} disabled={isDisabled}>
          {t('vote')}
        </SubmitButton>
      )}
      {poll.endDate && !hasExpired && !isStopped && (
        <VoteDetails>
          {t('active_till', { date: formattedEndDate })}
        </VoteDetails>
      )}
      {(hasExpired || isStopped) && (
        <VoteDetails>{t('poll_ended')}</VoteDetails>
      )}
    </BodyContainer>
  );
};

export default PollBody;
